import { FC, ReactNode, useEffect } from 'react';

import { toast } from 'react-toastify';

import { IntegrationStatus } from '@/modules/common/types/integration-status';
import { IntegrationServices } from '@/modules/common/types/integrations';
import { ConnectionApiTokenModal } from '@/modules/onboarding/integrations-setup/components/modals/connection-api-token-modal';
import { ConnectionGoogleModal } from '@/modules/onboarding/integrations-setup/components/modals/connection-google-modal';
import { ConnectionModal } from '@/modules/onboarding/integrations-setup/components/modals/connection-modal';
import { useConfigureClockifyIntegration } from '@/modules/onboarding/integrations-setup/hooks/use-configure-clockify-integration';
import { useConfigureTempoIntegration } from '@/modules/onboarding/integrations-setup/hooks/use-configure-tempo-integration';
import { useIntegrationStatus } from '@/modules/onboarding/integrations-setup/hooks/use-integration-status';
import { useInitiateOAuthFlow } from '@/modules/settings/hooks';

interface IntegrationConnectionTriggerProps {
  service: IntegrationServices;
  /** Trigger element (only required when not using controlled mode) */
  children?: ReactNode;
  /** Controlled open state (optional) */
  open?: boolean;
  /** Callback when open state changes (optional) */
  onOpenChange?: (open: boolean) => void;
}

/**
 * Reusable component that wraps connection modals for integrations.
 * Handles routing to the appropriate modal based on the integration service.
 * Supports both controlled and uncontrolled modes.
 *
 * OAuth-based integrations (Jira, QuickBooks): Redirect immediately to OAuth flow
 * Form-based integrations (Google): Use custom connection forms
 * Other integrations: Use generic connection modal
 *
 * @example
 * ```tsx
 * // Uncontrolled (trigger-based) - modal controlled by trigger button
 * <IntegrationConnectionTrigger service={IntegrationServices.GOOGLE}>
 *   <Button>Connect</Button>
 * </IntegrationConnectionTrigger>
 *
 * // Controlled (state-based) - for OAuth, immediately redirects when open=true
 * <IntegrationConnectionTrigger
 *   service={IntegrationServices.JIRA}
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 * />
 * ```
 */
export const IntegrationConnectionTrigger: FC<IntegrationConnectionTriggerProps> = ({
  service,
  children,
  open,
  onOpenChange,
}) => {
  const { initiateOAuth } = useInitiateOAuthFlow();
  const { data: integrationStatusData } = useIntegrationStatus({ enablePolling: false });

  // Check if Jira is connected (required for Tempo)
  const isJiraConnected =
    integrationStatusData?.integrations.find(
      integration => integration.integration === IntegrationServices.JIRA
    )?.status === IntegrationStatus.Connected;

  // Handle OAuth integrations - redirect immediately when opened
  useEffect(() => {
    if (
      open &&
      (service === IntegrationServices.JIRA || service === IntegrationServices.QUICKBOOKS)
    ) {
      // Immediately initiate OAuth flow
      initiateOAuth(service);
      // Close the modal state
      onOpenChange?.(false);
    }
  }, [open, service, initiateOAuth, onOpenChange]);

  // Handle Tempo validation - check if Jira is connected
  useEffect(() => {
    if (open && service === IntegrationServices.TEMPO && !isJiraConnected) {
      // Show error toast
      toast.error('Jira integration is required to connect Tempo. Please connect Jira first.');
      // Close the modal state
      onOpenChange?.(false);
    }
  }, [open, service, isJiraConnected, onOpenChange]);

  // In controlled mode without children, render modal directly without trigger
  const trigger = children || <span style={{ display: 'none' }} />;

  // Route to appropriate modal based on integration type
  switch (service) {
    case IntegrationServices.GOOGLE:
      return (
        <ConnectionGoogleModal open={open} onOpenChange={onOpenChange}>
          {trigger}
        </ConnectionGoogleModal>
      );

    case IntegrationServices.TEMPO:
      return (
        <ConnectionApiTokenModal
          open={open}
          onOpenChange={onOpenChange}
          useConfigureApiTokenIntegration={useConfigureTempoIntegration}
          service={IntegrationServices.TEMPO}
        >
          {trigger}
        </ConnectionApiTokenModal>
      );

    case IntegrationServices.CLOCKIFY:
      return (
        <ConnectionApiTokenModal
          open={open}
          onOpenChange={onOpenChange}
          useConfigureApiTokenIntegration={useConfigureClockifyIntegration}
          service={IntegrationServices.CLOCKIFY}
        >
          {trigger}
        </ConnectionApiTokenModal>
      );

    case IntegrationServices.JIRA:
    case IntegrationServices.QUICKBOOKS:
      // OAuth-based integrations - no modal, handled by useEffect above
      // Return null since OAuth redirect happens immediately
      return null;

    default:
      // Generic connection modal for other integrations
      return (
        <ConnectionModal service={service} open={open} onOpenChange={onOpenChange}>
          {trigger}
        </ConnectionModal>
      );
  }
};
